export class UniqueArray<T> extends Array<T> {
	readonly [n: number]: T;
	override readonly length: number;

	constructor()
	/** This constructor expect unique values and will not remove duplicates,
	 * if the values are not unique use UniqueArray.from(...)
	 */
	constructor(...items: T[])
	constructor(...items: T[]) { super(...items); }

	get(index: number) { return this[index]; }

	/** This function will remove duplicates */
	public static override from<T>(arrayLike: ArrayLike<T>): UniqueArray<T> {
		let values: T[] = Array.from(arrayLike);
		if (values.length > 1 && values.some((value, index, self) => self.lastIndexOf(value) !== index)) {
			values = [...new Set(values)];
		}
		return new UniqueArray(...values);
	}

	override map<U>(callback: (value: T, index: number, array: T[]) => U, thisArg?: any): U[] {
		return Array.from(this, (v, i) => callback.call(thisArg, v, i, this));
	}

	public copy(): UniqueArray<T> {
		return new (UniqueArray as any)(...this);
	}

	// swap 2 elements?
	// swap at 2 positions?
	// swap 2 ranges 2-5 with 8-15

	override concat(...items: ConcatArray<T>[]): UniqueArray<T>;
	override concat(...items: (T | ConcatArray<T>)[]): UniqueArray<T>;
	override concat(...items: (T | ConcatArray<T>)[]) {
		// TODO: improve?
		// return this.copy().concat(...items).filter((v, i, self) => self.lastIndexOf(v) === i);
		let values: T[] = [...this].concat(...items);
		if (values.length > 1 && values.some((value, index, self) => self.lastIndexOf(value) !== index)) {
			values = [...new Set(values)];
		}
		return new UniqueArray<T>(...values);
	}

	override push(...items: T[]): number {
		if (items.length > 1 && items.some((value, index, self) => self.lastIndexOf(value) !== index)) {
			items = [...new Set(items)];
		}
		for (const item of items) {
			const at = this.indexOf(item);
			if (at > -1) {
				super.splice(at, 1);
			}
		}
		return super.push(...items);
	}

	override unshift(...items: T[]): number {
		if (items.length > 1 && items.some((value, index, self) => self.lastIndexOf(value) !== index)) {
			items = [...new Set(items)];
		}
		for (const item of items) {
			const at = this.indexOf(item);
			if (at > -1) {
				super.splice(at, 1);
			}
		}
		return super.unshift(...items);
	}

	insertAt(index: number, ...items: T[]) {
		if (items.length > 1 && items.some((value, index, self) => self.lastIndexOf(value) !== index)) {
			items = [...new Set(items)];
		}
		for (const item of items) {
			const at = this.indexOf(item);
			if (at > -1) {
				super.splice(at, 1);
				if (at < index) {
					index--;
				}
			}
		}
		super.splice(index, 0, ...items);
	}

	removeElement(element: T) {
		const at = this.indexOf(element);
		if (at > -1) {
			super.splice(at, 1);
			return true;
		}
		return false;
	}

	removeAt(index: number) {
		if (index < this.length) {
			super.splice(index, 1);
			index--;
			return true;
		}
		return false;
	}

	clear() {
		super.splice(0, this.length);
	}

	/** @deprecated This function doesn't work with UniqueArray (or it's usage would be confusing) */
	override fill(): this { throw new Error("Intentionally disabled"); }

	/** @deprecated This function doesn't work with UniqueArray (or it's usage would be confusing) */
	override copyWithin(): this { throw new Error("Intentionally disabled"); }

	/** @deprecated This function doesn't work with UniqueArray (or it's usage would be confusing) */
	override splice(start: number, deleteCount?: number): T[];
	/** @deprecated This function doesn't work with UniqueArray (or it's usage would be confusing) */
	override splice(start: number, deleteCount: number, ...items: T[]): T[];
	override splice(): this { throw new Error("Intentionally disabled"); }
}

export interface UniqueArray<T> {
	filter<S extends T>(predicate: (value: T, index: number, array: T[]) => value is S, thisArg?: any): UniqueArray<S>;
	filter(predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: any): UniqueArray<T>;
	reverse(): UniqueArray<T>;
	slice(start?: number, end?: number): UniqueArray<T>;

	every<S extends T>(predicate: (value: T, index: number, array: T[]) => value is S, thisArg?: any): this is UniqueArray<S>;
	every(predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: any): boolean;
}
